import _ from 'underscore';
import {
  iffInProgressSyntax, iffInProgressAudienceSyntax,
} from './regex/iff-in-progress-syntax';
import { audienceListSyntax } from './regex/audience-list-syntax';
import { unescapeIff } from './escape';
import { IF_FUNCTION_TYPE } from './if-function-type';

const SPECIFICITY = {
  TYPE: 1,
};

/**
 * Removes spaces before and after the text, as well as
 * keeps only one space between words.
 * @param {String} text the text to normalize
 * @returns {String} the normalized text
 */
export function normalizeInsertText(text) {
  return text.trim().replace(/(?:\s+)/g, ' ');
}

/**
 * Unescapes and normalizes insert or default text.
 * @param {String} text an insert or default text to parse
 * @returns {String} the parsed text
 */
export function parseInsertText(text) {
  const unescapedText = unescapeIff(text);
  return normalizeInsertText(unescapedText);
}

/**
 * Looks for an incomplete device-based IF-function rule definition in given text,
 * starting from the passed index. If found, it returns the rule's paramenters/parts.
 * Otherwise, returns null.
 * @param {Object} options the options
 * @param {String} options.text the whole text
 * @param {Number} options.startIndex the 0-based index of '{' in the whole text. Optionally,
 * relative to the cursorIndex.
 * @param {Number} [options.cursorIndex] the 0-based position of the cursor in the whole text. Can
 * be undefined for scenarios where we are parsing the expression for preview, where there is no
 * cursor
 * @returns {Object} parsed rule definition parameters, or null, if a definiton is not found.
 */
export function parseIffDeviceRuleInProgress({
  text, startIndex = 0, cursorIndex,
}) {
  const input = text.substring(startIndex);
  const tokens = iffInProgressSyntax.exec(input);

  if ((tokens === null) ||
    (cursorIndex && cursorIndex <= (startIndex + tokens.index))) {
    return null;
  }

  const type = tokens[2] ? IF_FUNCTION_TYPE.DEVICE_MOBILE : IF_FUNCTION_TYPE.UNKNOWN;

  /* eslint-disable no-bitwise */
  const specificity = (type && type !== IF_FUNCTION_TYPE.UNKNOWN && SPECIFICITY.TYPE) |
    (0);
  /* eslint-enable no-bitwise */


  return {
    defaultText: tokens[4] ? parseInsertText(tokens[4]) : undefined,
    insertText: tokens[3] ? parseInsertText(tokens[3]) : undefined,
    isStarted: !!tokens[1],
    isComplete: !!tokens[5],
    source: tokens[0],
    specificity,
    type,
    index: startIndex + tokens.index,
    startIndex,
  };
}

/**
 * Parses the raw audience list part of the audience-based
 * IF-function into an array of audience names
 * @param {String} audienceListRaw the raw audience list text
 * @returns {String[]} the array of audience names
 */
export function parseAudienceList(audienceListRaw) {
  const audienceList = [];
  let tokens;
  // eslint-disable-next-line no-cond-assign
  while ((tokens = audienceListSyntax.exec(audienceListRaw)) !== null) {
    // This is necessary to avoid infinite loops with zero-width matches
    if (tokens.index === audienceListSyntax.lastIndex) {
      audienceListSyntax.lastIndex += 1;
    }

    const finalAudience = unescapeIff(tokens[0]).trim();
    // Ignoring empty entries
    if (finalAudience) {
      audienceList.push(finalAudience);
    }
  }

  return audienceList;
}

/**
 * Looks for an incomplete audience-based IF-function rule definition in given text,
 * starting from the passed index. If found, it returns the rule's paramenters/parts.
 * Otherwise, returns null.
 * @param {Object} options the options
 * @param {String} options.text the whole text
 * @param {Number} options.startIndex the 0-based index of '{' in the whole text. Optionally,
 * relative to the cursorIndex.
 * @param {Number} [options.cursorIndex] the 0-based position of the cursor in the whole text. Can
 * be undefined for scenarios where we are parsing the expression for preview, where there is no
 * cursor
 * @returns {Object} parsed rule definition parameters, or null, if a definiton is not found.
 */
export function parseIffAudienceRuleInProgress({
  text, startIndex = 0, cursorIndex,
}) {
  const input = text.substring(startIndex);
  const tokens = iffInProgressAudienceSyntax.exec(input);

  if ((tokens === null) ||
    (cursorIndex && cursorIndex <= (startIndex + tokens.index))) {
    return null;
  }

  const type = tokens[2] ? IF_FUNCTION_TYPE.AUDIENCE_IN : IF_FUNCTION_TYPE.UNKNOWN;

  /* eslint-disable no-bitwise */
  const specificity = (type && type !== IF_FUNCTION_TYPE.UNKNOWN && SPECIFICITY.TYPE) |
    (0);
  /* eslint-enable no-bitwise */

  return {
    audienceList: tokens[3] ? parseAudienceList(tokens[3]) : [],
    audienceListRaw: tokens[3],
    defaultText: tokens[5] ? parseInsertText(tokens[5]) : undefined,
    insertText: tokens[4] ? parseInsertText(tokens[4]) : undefined,
    isStarted: !!tokens[1],
    isComplete: !!tokens[6],
    source: tokens[0],
    specificity,
    type,
    index: startIndex + tokens.index,
    startIndex,
  };
}

export function parseRule({
  text, startIndex, cursorIndex,
}) {
  const parserArgs = {
    text, startIndex, cursorIndex,
  };
  const parsers = [
    parseIffDeviceRuleInProgress,
    parseIffAudienceRuleInProgress,
  ];

  return _.chain(parsers)
    .map(parser => parser(parserArgs))
    .sortBy(_.property('specificity'))
    .last()
    .value();
}
